import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  MenuItem,
  Modal,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Button,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useDropzone } from "react-dropzone";
import api from "../../../src/utils/api";
import { ThemeContext } from "../../utils/ThemeContext";

const EditViolation = ({ violationDetails, onUpdate }) => {
  const { isDark } = React.useContext(ThemeContext);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(
    violationDetails.status || "Open"
  );
  const [commentText, setCommentText] = useState("");
  const [commentType, setCommentType] = useState("internal");
  const [pendingCommentType, setPendingCommentType] = useState("internal");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const storedUser = sessionStorage.getItem("user");
  let currentUserId = null;
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      currentUserId = parsedUser?.userId ?? null;
    } catch (e) {
      console.error("Failed to parse user from sessionStorage", e);
    }
  }

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await api.get("/statuses/violations");
        setStatusOptions(res.data || []);
      } catch (error) {
        console.error("Error fetching statuses:", error);
        setStatusOptions([
          { statusname: "Open" },
          { statusname: "Closed" },
          { statusname: "Regression" },
        ]);
      }
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    setUpdatedStatus(violationDetails.status || "Open");
    setCommentText("");
    setCommentType("internal");
    setSelectedFiles([]);
  }, [violationDetails]);

  const handleEditModalOpen = () => setIsEditModalOpen(true);

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setCommentText("");
    setCommentType("internal");
    setSelectedFiles([]);
  };

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCommentTypeChange = (value) => {
    if (value === "external") {
      setPendingCommentType(value);
      setConfirmExternalOpen(true);
    } else {
      setCommentType("internal");
    }
  };

  const confirmExternal = () => {
    setCommentType(pendingCommentType);
    setConfirmExternalOpen(false);
  };

  const cancelExternal = () => {
    setCommentType("internal");
    setConfirmExternalOpen(false);
  };

  const handleSaveChanges = async () => {
    const updatedData = {
      resolved_by: updatedStatus === "Closed" ? currentUserId : null,
      status: updatedStatus,
      comment_text: commentText,
      is_internal: commentType === "internal" ? 1 : 0,
      user_id: currentUserId,
    };

    try {
      setIsSaving(true);

      const res = await api.put(
        `/editViolationstatus/${violationDetails.id}`,
        updatedData
      );
      const commentId = res.data.comment_id || null;

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("request_id", violationDetails.id);
        formData.append("uploaded_by", currentUserId);
        formData.append("comment_id", commentId ? String(commentId) : "");

        selectedFiles.forEach((file) => {
          formData.append("upload_urls", file);
        });

        await api.post("/violation-files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      onUpdate(updatedData);

      setSnackbar({
        open: true,
        message: "Violation updated successfully!",
        severity: "success",
      });

      handleEditModalClose();
    } catch (error) {
      console.error("Error updating violation:", error);
      setSnackbar({
        open: true,
        message: "Failed to update violation or upload files.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const modalCardSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "92%", sm: 520 },
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: isDark ? "#1a2c46" : "rgba(255,255,255,0.98)",
    borderRadius: "18px",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(47,99,173,0.08)",
    boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.22)",
  };

  const sectionHeaderSx = {
    px: { xs: 2, sm: 2.5, md: 3 },
    pt: 2.5,
    pb: 1.5,
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(47,99,173,0.08)",
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(47,99,173,0.02)",
  };

  const contentWrapSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(47,99,173,0.18)",
    color: isDark ? "#b8c9de" : "#223142",
    "&:hover": {
      borderColor: isDark ? "#7ab3f0" : "#2f63ad",
      color: isDark ? "#dde5f0" : "#2f63ad",
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    backgroundColor: isDark ? "#1a2c46" : "rgba(255,255,255,0.98)",
    boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.22)",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Button
        variant="contained"
        onClick={handleEditModalOpen}
        startIcon={<AutorenewIcon />}
        sx={primaryButtonSx}
      >
        Change Status
      </Button>

      <Modal open={isEditModalOpen} onClose={handleEditModalClose}>
        <Box sx={modalCardSx}>
          <Box sx={sectionHeaderSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <EditNoteIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142" }}>
                Edit Violation Status
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
              Update the violation status, add comments, and attach supporting files.
            </Typography>
          </Box>

          <Box sx={contentWrapSx}>
            <FormControl fullWidth sx={{ mb: 2.25 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={updatedStatus}
                onChange={(e) => setUpdatedStatus(e.target.value)}
                label="Status"
                sx={textFieldSx}
              >
                {statusOptions.map((status) => (
                  <MenuItem
                    key={status.id || status.statusname}
                    value={status.statusname}
                  >
                    {status.statusname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Add Comment"
              multiline
              rows={4}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              fullWidth
              sx={{ ...textFieldSx, mb: 2.25 }}
            />

            <FormControl fullWidth sx={{ mb: 2.25 }}>
              <InputLabel>Comment Type</InputLabel>
              <Select
                value={commentType}
                onChange={(e) => handleCommentTypeChange(e.target.value)}
                label="Comment Type"
                sx={textFieldSx}
              >
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ mb: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 600, color: isDark ? "#dde5f0" : "#223142" }}
            >
              Attach Files
            </Typography>

            <Paper
              variant="outlined"
              {...getRootProps()}
              sx={{
                p: 2.5,
                border: isDark ? "2px dashed rgba(122,179,240,0.35)" : "2px dashed #90caf9",
                borderRadius: "14px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive
                  ? (isDark ? "rgba(122,179,240,0.1)" : "#e3f2fd")
                  : (isDark ? "rgba(255,255,255,0.03)" : "#fafafa"),
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(122,179,240,0.08)" : "#f5faff",
                  borderColor: isDark ? "#7ab3f0" : "#1976d2",
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop files here..."
                  : "Drag & drop files here, or click to select"}
              </Typography>
            </Paper>

            {selectedFiles.length > 0 && (
              <List
                dense
                sx={{
                  mt: 1.5,
                  maxHeight: 150,
                  overflowY: "auto",
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "12px",
                  backgroundColor: "rgba(47,99,173,0.02)",
                  px: 1,
                }}
              >
                {selectedFiles.map((file, idx) => (
                  <ListItem
                    key={`${file.name}-${idx}`}
                    sx={{
                      borderBottom:
                        idx !== selectedFiles.length - 1
                          ? "1px solid rgba(47,99,173,0.08)"
                          : "none",
                    }}
                  >
                    <ListItemText
                      primary={file.name}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: { color: isDark ? "#dde5f0" : "#223142", fontWeight: 500 },
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleRemoveFile(idx)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.25,
                mt: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleEditModalClose}
                sx={secondaryButtonSx}
                disabled={isSaving}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={isSaving}
                sx={primaryButtonSx}
              >
                {isSaving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Submit Changes"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={confirmExternalOpen}
        onClose={cancelExternal}
        PaperProps={{ sx: dialogPaperSx }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: isDark ? "#dde5f0" : "#223142",
            pt: 3,
          }}
        >
          <WarningAmberIcon sx={{ color: "#ed6c02" }} />
          Send to Homeowner?
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
            External comments will be emailed to the homeowner. Continue?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={cancelExternal} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmExternal} variant="contained" sx={primaryButtonSx}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
          onClose={handleSnackbarClose}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditViolation;